'use es6';

import { createSelector } from 'reselect';
import { memoize } from 'underscore';
import { LAYOUT, CUSTOM_WIDGET, GLOBAL_WIDGET, GLOBAL_GROUP, LAYOUT_SOURCE, TEMPLATE, RAW_ASSET, FOLDER } from 'DesignData/enums/DesignAssetTypes';
import { SCROLL_ACTIVE_TAB_INTO_VIEW } from 'DesignData/enums/FlashKeys';
import { getFolder } from 'DesignData/selectors/finder';
import { getLayoutsById, getLayout } from 'DesignData/selectors/layouts';
import { getTemplateBuffer } from 'DesignData/selectors/templateBuffers';
import { getRawAsset } from 'DesignData/selectors/rawAssetBuffers';
import { makeGetCustomWidgetBuffer } from 'DesignData/selectors/customWidgetBuffers';
import { getGlobalWidget } from 'DesignData/selectors/globalWidgets';
export const getTabs = state => state.tabs;
export const getTabsById = state => state.tabs.byId;
export const getTabsOrder = state => state.tabs.order;
export const getActiveTabId = state => state.tabs.activeTabId;

const getLastOpenTabId = state => state.tabs.lastOpen;

export const getShouldScrollActiveTabIntoView = state => state.tabs.shouldScrollActiveTabIntoView;
export const getRedirectedTabs = state => state.tabs.redirects;

const getCustomWidgetsById = state => state.customWidgetBuffers.byId;

const getGlobalWidgetsById = state => state.globalWidgets.byId;

const getTemplatesById = state => state.templateBuffers.byId;

export const getTab = (state, props) => getTabsById(state)[props.tabId];
export const getLastOpenTab = createSelector([getTabsById, getLastOpenTabId], (tabs, lastOpenTabId) => {
  return tabs[lastOpenTabId];
});
export const getIsScrollActiveTabIntoViewRequested = state => {
  return state.flash[SCROLL_ACTIVE_TAB_INTO_VIEW];
};
export const getShowCompiledOutputOverride = (state, props) => {
  const tabId = props.assetId || props.templateId;
  const tab = getTabsById(state)[tabId];
  const tabEditorOptions = tab && tab.editorOptions;
  return tabEditorOptions ? !!tabEditorOptions.showCompiledOutput : false;
};
export const makeGetIsTabPinned = createSelector([getTabsById], tabsById => tabId => {
  const tab = tabsById[tabId];
  return tab && tab.isPinned;
});
export const getActiveTabAssetType = createSelector([getTabsById, getActiveTabId], (tabsById, activeTabId) => {
  if (activeTabId) {
    const activeTab = tabsById[activeTabId];

    if (activeTab) {
      return activeTab.assetType;
    }
  }

  return null;
});
/**
 * Check if asset has buffered changes
 * @return {Boolean}
 */

export const hasBufferedChanges = createSelector([getTabsById, getLayoutsById, getCustomWidgetsById, getGlobalWidgetsById, getTemplatesById], (tabs, layoutsById, customWidgetsById, globalWidgetsById, templatesById) => memoize(tabId => {
  const tab = tabs[tabId];

  if (!tab) {
    return false;
  }

  const {
    assetType,
    assetId
  } = tab;

  switch (assetType) {
    case LAYOUT:
      {
        const layout = layoutsById[assetId];
        return !!(layout && layout.meta.hasBufferedChanges);
      }

    case CUSTOM_WIDGET:
      {
        const customWidget = customWidgetsById[assetId] && customWidgetsById[assetId].present;
        const customWidgetBuffer = customWidget && customWidget.buffer;
        return !!(customWidgetBuffer && customWidgetBuffer.isBuffered);
      }

    case GLOBAL_WIDGET:
      {
        const globalWidget = globalWidgetsById[assetId] && globalWidgetsById[assetId].present;
        return !!(globalWidget && globalWidget.hasEdits);
      }

    case TEMPLATE:
      {
        const template = templatesById[assetId];
        return !!(template && template.meta.hasBufferedChanges);
      }

    default:
      return false;
  }
}));
/**
 * Required Props:
 *  assetId
 *  assetType
 * Optional Props:
 *  revisionId
 *
 * @return {Function}
 */

export const makeGetTabAsset = () => {
  let getCustomWidgetBuffer;

  const getSelector = assetType => {
    switch (assetType) {
      case LAYOUT:
      case LAYOUT_SOURCE:
      case GLOBAL_GROUP:
        {
          return getLayout;
        }

      case TEMPLATE:
        {
          return getTemplateBuffer;
        }

      case CUSTOM_WIDGET:
        {
          return getCustomWidgetBuffer || (getCustomWidgetBuffer = makeGetCustomWidgetBuffer());
        }

      case GLOBAL_WIDGET:
        {
          return getGlobalWidget;
        }

      case RAW_ASSET:
        {
          return getRawAsset;
        }

      case FOLDER:
        {
          return getFolder;
        }

      default:
        return getTab;
    }
  };

  return (state, props) => getSelector(props.assetType)(state, props);
};
export const getParentIdOfRedirectedTab = (state, props) => {
  const {
    tabId
  } = props;
  const redirects = getRedirectedTabs(state);
  const redirectedValues = Object.values(redirects);
  const redirectedTabIdIndex = redirectedValues.indexOf(tabId);

  if (redirectedTabIdIndex !== -1) {
    return parseInt(Object.keys(redirects)[redirectedTabIdIndex], 10);
  }

  return null;
};